import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users, UserPlus, Search, Mail, Phone, Calendar, Shield, BookOpen, Edit, Trash2, MoreHorizontal, UserCheck, Key, RefreshCw, Eye, EyeOff, Copy, CheckCircle, XCircle, Globe, Circle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { usePresence } from '@/hooks/usePresence';
import { LecturerPrintCard, BulkLecturerCardExport } from './LecturerPrintCard';

interface Lecturer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  created_at: string;
  groups_count: number;
  active_lessons: number;
  approval_status: string;
  avatar_url?: string;
}

interface Group {
  id: string;
  name: string;
  lecturer_id: string;
  secondary_lecturers: string[];
  student_count: number;
  lecturer_name?: string;
  secondary_lecturer_names?: Array<{id: string, name: string}>;
}

export const LecturersManagementSection = () => {
  const [lecturers, setLecturers] = useState<Lecturer[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAssignGroupDialogOpen, setIsAssignGroupDialogOpen] = useState(false);
  const [selectedLecturerId, setSelectedLecturerId] = useState('');
  const [editingLecturer, setEditingLecturer] = useState<Lecturer | null>(null);
  const [isCredentialsDialogOpen, setIsCredentialsDialogOpen] = useState(false);
  const [isIndividualCredentialsDialogOpen, setIsIndividualCredentialsDialogOpen] = useState(false);
  const [selectedLecturerCredentials, setSelectedLecturerCredentials] = useState<any>(null);
  const [lecturerCredentials, setLecturerCredentials] = useState<any[]>([]);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newLecturer, setNewLecturer] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const { toast } = useToast();
  const { onlineUsers } = usePresence();

  const fetchLecturers = async () => {
    try {
      setLoading(true);
      
      // First get lecturer user IDs from user_roles
      const { data: lecturerRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'lecturer');

      if (rolesError) throw rolesError;

      if (!lecturerRoles || lecturerRoles.length === 0) {
        setLecturers([]);
        return;
      }

      const lecturerIds = lecturerRoles.map(role => role.user_id);

      // Then get profiles for those users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', lecturerIds);

      if (profilesError) throw profilesError;

      // Get additional data for each lecturer
      const lecturersWithStats = await Promise.all(
        (profiles || []).map(async (lecturer) => {
          // Count groups where lecturer is primary or secondary
          const { count: groupsCount } = await supabase
            .from('groups')
            .select('*', { count: 'exact', head: true })
            .or(`lecturer_id.eq.${lecturer.id},secondary_lecturers.cs.{${lecturer.id}}`);

          // Count active lessons where lecturer is primary or in any group they're assigned to
          const { data: lecturerGroups } = await supabase
            .from('groups')
            .select('id')
            .or(`lecturer_id.eq.${lecturer.id},secondary_lecturers.cs.{${lecturer.id}}`);
          
          const groupIds = lecturerGroups?.map(g => g.id) || [];
          let lessonsCount = 0;
          
          if (groupIds.length > 0) {
            const { count } = await supabase
              .from('lessons')
              .select('*', { count: 'exact', head: true })
              .in('group_id', groupIds)
              .eq('status', 'scheduled');
            lessonsCount = count || 0;
          }

          // Get avatar from storage if exists
          const { data: avatarData } = await supabase.storage
            .from('avatars')
            .list(`${lecturer.id}/`, {
              limit: 1,
              sortBy: { column: 'updated_at', order: 'desc' }
            });

          const avatarUrl = avatarData && avatarData.length > 0 
            ? supabase.storage.from('avatars').getPublicUrl(`${lecturer.id}/${avatarData[0].name}`).data.publicUrl
            : undefined;

          return {
            ...lecturer,
            groups_count: groupsCount || 0,
            active_lessons: lessonsCount,
            approval_status: lecturer.approval_status || 'pending',
            avatar_url: avatarUrl
          };
        })
      );

      setLecturers(lecturersWithStats);
    } catch (error) {
      console.error('Error fetching lecturers:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել դասախոսների ցանկը",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const { data: groupsData, error: groupsError } = await supabase
        .from('groups')
        .select('*')
        .order('created_at', { ascending: false });

      if (groupsError) throw groupsError;

      // Get student counts and lecturer info for each group
      const groupsWithCounts = await Promise.all(
        (groupsData || []).map(async (group) => {
          const { count, error: countError } = await supabase
            .from('group_students')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id);
          
          // Get primary lecturer info if lecturer_id exists
          let lecturerName = null;
          if (group.lecturer_id) {
            const { data: lecturerData, error: lecturerError } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', group.lecturer_id)
              .single();
            
            if (!lecturerError && lecturerData) {
              lecturerName = `${lecturerData.first_name} ${lecturerData.last_name}`;
            }
          }
          
          // Get secondary lecturers info
          let secondaryLecturerNames: Array<{id: string, name: string}> = [];
          if (group.secondary_lecturers && group.secondary_lecturers.length > 0) {
            const { data: secondaryData, error: secondaryError } = await supabase
              .from('profiles')
              .select('id, first_name, last_name')
              .in('id', group.secondary_lecturers);
            
            if (!secondaryError && secondaryData) {
              secondaryLecturerNames = secondaryData.map(lecturer => ({
                id: lecturer.id,
                name: `${lecturer.first_name} ${lecturer.last_name}`
              }));
            }
          }
          
          return {
            ...group,
            student_count: count || 0,
            lecturer_name: lecturerName,
            secondary_lecturer_names: secondaryLecturerNames
          };
        })
      );

      setGroups(groupsWithCounts);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchLecturerCredentials = async () => {
    try {
      const { data, error } = await supabase
        .from('lecturer_invitations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Get lecturer details for each invitation
      const credentialsWithDetails = await Promise.all(
        (data || []).map(async (credential) => {
          const { data: lecturerData, error: lecturerError } = await supabase
            .from('profiles')
            .select('first_name, last_name, email')
            .eq('id', credential.lecturer_id)
            .single();
          
          return {
            ...credential,
            lecturer: lecturerError ? null : lecturerData
          };
        })
      );
      
      setLecturerCredentials(credentialsWithDetails);
    } catch (error) {
      console.error('Error fetching lecturer credentials:', error);
    }
  };

  const handleResetPassword = async (lecturerId: string) => {
    setIsResettingPassword(true);
    try {
      // Generate new temporary password
      const newPassword = Math.random().toString(36).slice(-8);
      
      // Reset user password via admin
      const { error: resetError } = await supabase.auth.admin.updateUserById(lecturerId, {
        password: newPassword
      });

      if (resetError) throw resetError;

      // Update invitation record
      const { error: updateError } = await supabase
        .from('lecturer_invitations')
        .update({ 
          temporary_password: newPassword,
          password_changed: false,
          used_at: null
        })
        .eq('lecturer_id', lecturerId);

      if (updateError) throw updateError;

      toast({
        title: "Հաջողություն",
        description: `Գաղտնաբառը վերականգնվեց: ${newPassword}`,
        duration: 10000
      });

      fetchLecturerCredentials();
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        title: "Սխալ",
        description: error.message || "Չհաջողվեց վերականգնել գաղտնաբառը",
        variant: "destructive"
      });
    } finally {
      setIsResettingPassword(false);
      // Refresh individual credentials if dialog is open
      if (selectedLecturerCredentials) {
        await handleViewLecturerCredentials(selectedLecturerCredentials.lecturer_id);
      }
    }
  };

  const handleApproveLecturer = async (lecturerId: string) => {
    try {
      const { data, error } = await supabase.rpc('approve_user', {
        user_id_to_approve: lecturerId,
        approver_user_id: (await supabase.auth.getUser()).data.user?.id
      });

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Դասախոսը հաջողությամբ հաստատվեց"
      });

      fetchLecturers();
    } catch (error: any) {
      console.error('Error approving lecturer:', error);
      toast({
        title: "Սխալ",
        description: error.message || "Չհաջողվեց հաստատել դասախոսին",
        variant: "destructive"
      });
    }
  };

  const handleRejectLecturer = async (lecturerId: string) => {
    try {
      const { data, error } = await supabase.rpc('reject_user', {
        user_id_to_reject: lecturerId,
        rejector_user_id: (await supabase.auth.getUser()).data.user?.id
      });

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Դասախոսը մերժվեց"
      });

      fetchLecturers();
    } catch (error: any) {
      console.error('Error rejecting lecturer:', error);
      toast({
        title: "Սխալ",
        description: error.message || "Չհաջողվեց մերժել դասախոսին",
        variant: "destructive"
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Պատճենվեց",
      description: "Տեքստը պատճենվեց clipboard-ում",
    });
  };

  const handleViewLecturerCredentials = async (lecturerId: string) => {
    try {
      const { data, error } = await supabase
        .from('lecturer_invitations')
        .select('*')
        .eq('lecturer_id', lecturerId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      
      if (data && data.length > 0) {
        // Get lecturer details
        const { data: lecturerData, error: lecturerError } = await supabase
          .from('profiles')
          .select('first_name, last_name, email')
          .eq('id', lecturerId)
          .single();
        
        const credentialWithDetails = {
          ...data[0],
          lecturer: lecturerError ? null : lecturerData
        };
        
        setSelectedLecturerCredentials(credentialWithDetails);
        setIsIndividualCredentialsDialogOpen(true);
      } else {
        // No invitation record exists, create one with new password
        const newPassword = Math.random().toString(36).slice(-8);
        
        // Get lecturer details first
        const { data: lecturerData, error: lecturerError } = await supabase
          .from('profiles')
          .select('first_name, last_name, email')
          .eq('id', lecturerId)
          .single();

        if (lecturerError) throw lecturerError;

        // Create new invitation record
        const { data: newInvitation, error: insertError } = await supabase
          .from('lecturer_invitations')
          .insert({
            lecturer_id: lecturerId,
            lecturer_email: lecturerData.email,
            temporary_password: newPassword,
            created_by: (await supabase.auth.getUser()).data.user?.id,
            password_changed: false
          })
          .select()
          .single();

        if (insertError) throw insertError;

        // Reset user password
        await supabase.auth.admin.updateUserById(lecturerId, {
          password: newPassword
        });

        const credentialWithDetails = {
          ...newInvitation,
          lecturer: lecturerData
        };
        
        setSelectedLecturerCredentials(credentialWithDetails);
        setIsIndividualCredentialsDialogOpen(true);

        toast({
          title: "Տեղեկություն",
          description: `Նոր ժամանակավոր գաղտնաբառ ստեղծվեց: ${newPassword}`,
          duration: 10000
        });
      }
    } catch (error) {
      console.error('Error fetching lecturer credentials:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել մուտքի տվյալները",
        variant: "destructive"
      });
    }
  };

  const isUserOnline = (userId: string) => {
    return onlineUsers[userId] && onlineUsers[userId].length > 0;
  };

  const getLastSeen = (userId: string): Date | null => {
    const userPresences = onlineUsers[userId];
    if (!userPresences || userPresences.length === 0) return null;
    return new Date(userPresences[0].online_at);
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-primary/10 text-primary border-border/70';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border-red-400/30';
      default:
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Հաստատված';
      case 'rejected':
        return 'Մերժված';
      default:
        return 'Սպասող';
    }
  };

  useEffect(() => {
    fetchLecturers();
    fetchGroups();
    fetchLecturerCredentials();
  }, []);

  const handleCreateLecturer = async () => {
    try {
      // Use edge function to create lecturer with service role
      const { data, error } = await supabase.functions.invoke('create-lecturer', {
        body: {
          firstName: newLecturer.first_name,
          lastName: newLecturer.last_name,
          email: newLecturer.email,
          phone: newLecturer.phone
        }
      });

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Դասախոսը հաջողությամբ ստեղծվեց։ Մուտքի տվյալները ուղարկվել են էլ. փոստով։",
        duration: 6000
      });

      setIsCreateDialogOpen(false);
      setNewLecturer({ first_name: '', last_name: '', email: '', phone: '' });
      fetchLecturers();
      fetchLecturerCredentials(); // Refresh credentials list
    } catch (error: any) {
      console.error('Error creating lecturer:', error);
      toast({
        title: "Սխալ",
        description: error.message || "Չհաջողվեց ստեղծել դասախոսին",
        variant: "destructive"
      });
    }
  };

  const handleEditLecturer = async () => {
    if (!editingLecturer) return;
    
    try {
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: editingLecturer.first_name,
          last_name: editingLecturer.last_name,
          phone: editingLecturer.phone || null
        })
        .eq('id', editingLecturer.id);

      if (profileError) throw profileError;

      toast({
        title: "Հաջողություն",
        description: "Դասախոսի տվյալները հաջողությամբ թարմացվեցին"
      });

      setIsEditDialogOpen(false);
      setEditingLecturer(null);
      fetchLecturers();
    } catch (error) {
      console.error('Error updating lecturer:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց թարմացնել դասախոսի տվյալները",
        variant: "destructive"
      });
    }
  };

  const handleDeleteLecturer = async (lecturerId: string) => {
    try {
      // Use the safe delete function that handles all deletions properly
      const { data, error } = await supabase.rpc('delete_lecturer_safely', {
        lecturer_user_id: lecturerId
      });

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Դասախոսը հաջողությամբ ջնջվեց"
      });

      fetchLecturers();
      fetchGroups(); // Also refresh groups since lecturer assignments may have changed
    } catch (error: any) {
      console.error('Error deleting lecturer:', error);
      toast({
        title: "Սխալ",
        description: error.message || "Չհաջողվեց ջնջել դասախոսին",
        variant: "destructive"
      });
    }
  };

  const handleAssignGroup = async (groupId: string) => {
    try {
      // First get the current group data to check if it has a primary lecturer
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select('lecturer_id, secondary_lecturers, name')
        .eq('id', groupId)
        .single();

      if (groupError) throw groupError;

      let updateData: any = {};
      let successMessage = "";

      // Check if lecturer is already assigned to this group
      const isAlreadyPrimary = groupData.lecturer_id === selectedLecturerId;
      const isAlreadySecondary = groupData.secondary_lecturers && 
        groupData.secondary_lecturers.includes(selectedLecturerId);

      if (isAlreadyPrimary) {
        toast({
          title: "Տեղեկություն",
          description: "Դասախոսն արդեն հանդիսանում է այս խմբի հիմնական դասախոս"
        });
        setIsAssignGroupDialogOpen(false);
        setSelectedLecturerId('');
        return;
      }

      if (isAlreadySecondary) {
        toast({
          title: "Տեղեկություն", 
          description: "Դասախոսն արդեն հանդիսանում է այս խմբի երկրորդական դասախոս"
        });
        setIsAssignGroupDialogOpen(false);
        setSelectedLecturerId('');
        return;
      }

      // Intelligent assignment decision
      if (!groupData.lecturer_id) {
        // No primary lecturer - assign as primary
        updateData.lecturer_id = selectedLecturerId;
        successMessage = "Դասախոսը նշանակվեց որպես խմբի հիմնական դասախոս";
      } else {
        // Has primary lecturer - add as secondary
        const currentSecondary = groupData.secondary_lecturers || [];
        updateData.secondary_lecturers = [...currentSecondary, selectedLecturerId];
        successMessage = "Դասախոսը ավելացվեց որպես խմբի երկրորդական դասախոս";
      }

      const { error } = await supabase
        .from('groups')
        .update(updateData)
        .eq('id', groupId);

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: successMessage
      });

      setIsAssignGroupDialogOpen(false);
      setSelectedLecturerId('');
      await fetchLecturers(); // Refresh lecturer data to update counts
      await fetchGroups(); // This will now show secondary lecturers
    } catch (error) {
      console.error('Error assigning group to lecturer:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց կցել խումբը դասախոսին",
        variant: "destructive"
      });
    }
  };

  const handleRemoveSecondaryLecturer = async (groupId: string, lecturerId: string) => {
    try {
      // Get current group data
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select('secondary_lecturers, name')
        .eq('id', groupId)
        .single();

      if (groupError) throw groupError;

      // Remove lecturer from secondary_lecturers array
      const updatedSecondaryLecturers = (groupData.secondary_lecturers || []).filter(
        (id: string) => id !== lecturerId
      );

      const { error: updateError } = await supabase
        .from('groups')
        .update({ secondary_lecturers: updatedSecondaryLecturers })
        .eq('id', groupId);

      if (updateError) throw updateError;

      toast({
        title: "Հաջողություն",
        description: "Երկրորդական դասախոսը հեռացվեց խմբից"
      });

      await fetchGroups();
      await fetchLecturers();
    } catch (error) {
      console.error('Error removing secondary lecturer:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց հեռացնել երկրորդական դասախոսին",
        variant: "destructive"
      });
    }
  };

  const filteredLecturers = lecturers.filter(lecturer =>
    lecturer.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lecturer.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lecturer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('hy-AM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Card className="border-border/70 bg-card/80">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Դասախոսների կառավարում ({lecturers.length})
          </CardTitle>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary h-4 w-4" />
              <Input
                placeholder="Փնտրել դասախոսի..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-muted border-border/70 text-foreground"
              />
            </div>
            {filteredLecturers.length > 0 && (
              <BulkLecturerCardExport 
                lecturers={filteredLecturers.map(l => ({
                  id: l.id,
                  first_name: l.first_name,
                  last_name: l.last_name,
                  email: l.email,
                  phone: l.phone,
                  skills: [],
                  avatar_url: l.avatar_url,
                  status: l.approval_status
                }))}
              />
            )}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Նոր դասախոս
                </Button>
              </DialogTrigger>
              <DialogContent className="border-border/70 bg-card/80">
                <DialogHeader>
                  <DialogTitle className="text-muted-foreground">Ստեղծել նոր դասախոս</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Անուն</Label>
                    <Input
                      value={newLecturer.first_name}
                      onChange={(e) => setNewLecturer({ ...newLecturer, first_name: e.target.value })}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Ազգանուն</Label>
                    <Input
                      value={newLecturer.last_name}
                      onChange={(e) => setNewLecturer({ ...newLecturer, last_name: e.target.value })}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Էլ. փոստ</Label>
                    <Input
                      type="email"
                      value={newLecturer.email}
                      onChange={(e) => setNewLecturer({ ...newLecturer, email: e.target.value })}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Հեռախոս</Label>
                    <Input
                      value={newLecturer.phone}
                      onChange={(e) => setNewLecturer({ ...newLecturer, phone: e.target.value })}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                  <Button
                    onClick={handleCreateLecturer}
                    className="w-full bg-primary hover:bg-primary/90"
                    disabled={!newLecturer.first_name || !newLecturer.last_name || !newLecturer.email}
                  >
                    Ստեղծել դասախոս
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            
            {/* Edit Lecturer Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
              <DialogContent className="border-border/70 bg-card/80">
                <DialogHeader>
                  <DialogTitle className="text-muted-foreground">Խմբագրել դասախոսին</DialogTitle>
                </DialogHeader>
                {editingLecturer && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Անուն</Label>
                      <Input
                        value={editingLecturer.first_name}
                        onChange={(e) => setEditingLecturer({ ...editingLecturer, first_name: e.target.value })}
                        className="bg-muted border-border/70 text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Ազգանուն</Label>
                      <Input
                        value={editingLecturer.last_name}
                        onChange={(e) => setEditingLecturer({ ...editingLecturer, last_name: e.target.value })}
                        className="bg-muted border-border/70 text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Էլ. փոստ</Label>
                      <Input
                        type="email"
                        value={editingLecturer.email}
                        disabled
                        className="bg-muted border-border/70 text-muted-foreground cursor-not-allowed"
                      />
                      <p className="text-xs text-muted-foreground">Էլ. փոստը հնարավոր չէ փոխել</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Հեռախոս</Label>
                      <Input
                        value={editingLecturer.phone || ''}
                        onChange={(e) => setEditingLecturer({ ...editingLecturer, phone: e.target.value })}
                        className="bg-muted border-border/70 text-foreground"
                      />
                    </div>
                    <Button
                      onClick={handleEditLecturer}
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={!editingLecturer.first_name || !editingLecturer.last_name}
                    >
                      Պահպանել փոփոխությունները
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Lecturer Credentials Management Dialog */}
            <Dialog open={isCredentialsDialogOpen} onOpenChange={setIsCredentialsDialogOpen}>
              <DialogContent className="border-border/70 bg-card/80 max-w-4xl">
                <DialogHeader>
                  <DialogTitle className="text-muted-foreground">Դասախոսների մուտքի տվյալներ</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {lecturerCredentials.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      Դասախոսների մուտքի տվյալներ չկան
                    </p>
                  ) : (
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {lecturerCredentials.map((credential) => (
                        <div key={credential.id} className="p-4 bg-muted rounded-lg border border-border/70">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div>
                                  <h4 className="text-foreground font-medium">
                                    {credential.lecturer?.first_name} {credential.lecturer?.last_name}
                                  </h4>
                                  <p className="text-muted-foreground text-sm">
                                    {credential.lecturer_email}
                                  </p>
                                </div>
                                <Badge className={`${
                                  credential.password_changed 
                                    ? 'bg-primary/10 text-primary border-border/70' 
                                    : 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30'
                                }`}>
                                  {credential.password_changed ? 'Փոխված' : 'Ժամանակավոր'}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                <div>
                                  <Label className="text-muted-foreground text-xs">Ժամանակավոր գաղտնաբառ</Label>
                                  <div className="flex items-center gap-2 mt-1">
                                    <code className="bg-muted px-2 py-1 rounded text-foreground text-sm flex-1">
                                      {credential.temporary_password}
                                    </code>
                                    <Button
                                      onClick={() => copyToClipboard(credential.temporary_password)}
                                      size="sm"
                                      variant="ghost"
                                      className="text-primary hover:text-muted-foreground"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground text-xs">Ստեղծման ամսաթիվ</Label>
                                  <p className="text-muted-foreground text-sm mt-1">
                                    {new Date(credential.created_at).toLocaleString('hy-AM')}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button
                                onClick={() => handleResetPassword(credential.lecturer_id)}
                                disabled={isResettingPassword}
                                size="sm"
                                variant="outline"
                                className="text-yellow-400 border-yellow-400 hover:bg-yellow-400/10"
                              >
                                <RefreshCw className={`h-4 w-4 mr-1 ${isResettingPassword ? 'animate-spin' : ''}`} />
                                Վերականգնել
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Individual Lecturer Credentials Dialog */}
            <Dialog open={isIndividualCredentialsDialogOpen} onOpenChange={setIsIndividualCredentialsDialogOpen}>
              <DialogContent className="border-border/70 bg-card/80 max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-muted-foreground">
                    Դասախոսի մուտքի տվյալներ
                  </DialogTitle>
                </DialogHeader>
                {selectedLecturerCredentials && (
                  <div className="space-y-4">
                    <div className="p-4 bg-muted rounded-lg border border-border/70">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="text-foreground font-medium text-lg">
                            {selectedLecturerCredentials.lecturer?.first_name} {selectedLecturerCredentials.lecturer?.last_name}
                          </h4>
                          <p className="text-muted-foreground">
                            {selectedLecturerCredentials.lecturer_email}
                          </p>
                        </div>
                        <Badge className={`${
                          selectedLecturerCredentials.password_changed 
                            ? 'bg-primary/10 text-primary border-border/70' 
                            : 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30'
                        }`}>
                          {selectedLecturerCredentials.password_changed ? 'Փոխված' : 'Ժամանակավոր'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <Label className="text-muted-foreground text-sm">Էլեկտրոնային փոստ</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="bg-muted px-3 py-2 rounded text-foreground flex-1">
                              {selectedLecturerCredentials.lecturer_email}
                            </code>
                            <Button
                              onClick={() => copyToClipboard(selectedLecturerCredentials.lecturer_email)}
                              size="sm"
                              variant="ghost"
                              className="text-primary hover:text-muted-foreground"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-muted-foreground text-sm">Ժամանակավոր գաղտնաբառ</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="bg-muted px-3 py-2 rounded text-foreground flex-1">
                              {showPassword ? selectedLecturerCredentials.temporary_password : '••••••••'}
                            </code>
                            <Button
                              onClick={() => setShowPassword(!showPassword)}
                              size="sm"
                              variant="ghost"
                              className="text-primary hover:text-muted-foreground"
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button
                              onClick={() => copyToClipboard(selectedLecturerCredentials.temporary_password)}
                              size="sm"
                              variant="ghost"
                              className="text-primary hover:text-muted-foreground"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-muted-foreground text-sm">Ստեղծման ամսաթիվ</Label>
                            <p className="text-muted-foreground text-sm mt-1">
                              {new Date(selectedLecturerCredentials.created_at).toLocaleString('hy-AM')}
                            </p>
                          </div>
                          {selectedLecturerCredentials.used_at && (
                            <div>
                              <Label className="text-muted-foreground text-sm">Օգտագործման ամսաթիվ</Label>
                              <p className="text-muted-foreground text-sm mt-1">
                                {new Date(selectedLecturerCredentials.used_at).toLocaleString('hy-AM')}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-3 pt-4 border-t border-border">
                          <Button
                            onClick={() => handleResetPassword(selectedLecturerCredentials.lecturer_id)}
                            disabled={isResettingPassword}
                            className="bg-yellow-600 hover:bg-yellow-700"
                          >
                            <RefreshCw className={`h-4 w-4 mr-2 ${isResettingPassword ? 'animate-spin' : ''}`} />
                            Վերականգնել գաղտնաբառ
                          </Button>
                          <Button
                            onClick={() => setIsIndividualCredentialsDialogOpen(false)}
                            variant="outline"
                            className="font-mono"
                          >
                            Փակել
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            
            {/* Assign Group to Lecturer Dialog */}
            <Dialog open={isAssignGroupDialogOpen} onOpenChange={setIsAssignGroupDialogOpen}>
              <DialogContent className="border-border/70 bg-card/80">
                <DialogHeader>
                  <DialogTitle className="text-muted-foreground">
                    Կցել խումբ դասախոսին
                    {selectedLecturerId && (
                      <span className="block text-sm text-muted-foreground mt-1">
                        {lecturers.find(l => l.id === selectedLecturerId)?.first_name} {lecturers.find(l => l.id === selectedLecturerId)?.last_name}
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Ընտրել խումբ</Label>
                    <div className="grid gap-2 max-h-60 overflow-y-auto">
                      <p className="text-muted-foreground text-sm mb-2">
                        Ընտրեք խումբ կցելու համար. կարող եք վերանշանակել ցանկացած խումբ:
                      </p>
                       {groups.map((group) => (
                         <div key={group.id} className="p-3 rounded-lg border border-border/70 bg-muted">
                           <div className="flex justify-between items-start mb-2">
                             <div className="flex-1">
                               <h4 className="text-foreground font-medium">{group.name}</h4>
                               <p className="text-muted-foreground text-sm">{group.student_count} ուսանող</p>
                               
                               {group.lecturer_name && (
                                 <p className="text-yellow-200 text-xs mt-1">
                                   <span className="font-semibold">Հիմնական:</span> {group.lecturer_name}
                                 </p>
                               )}
                               
                               {group.secondary_lecturer_names && group.secondary_lecturer_names.length > 0 && (
                                 <div className="mt-1">
                                   <p className="text-blue-200 text-xs font-semibold">Երկրորդական:</p>
                                   {group.secondary_lecturer_names.map((secondary) => (
                                     <div key={secondary.id} className="flex items-center justify-between mt-1">
                                       <span className="text-blue-200 text-xs ml-2">• {secondary.name}</span>
                                       <Button
                                         onClick={(e) => {
                                           e.stopPropagation();
                                           handleRemoveSecondaryLecturer(group.id, secondary.id);
                                         }}
                                         size="sm"
                                         variant="ghost"
                                         className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-400/10"
                                       >
                                         <XCircle className="h-3 w-3" />
                                       </Button>
                                     </div>
                                   ))}
                                 </div>
                               )}
                             </div>
                             <Button
                               onClick={() => handleAssignGroup(group.id)}
                               size="sm"
                               className="bg-primary hover:bg-primary/90"
                             >
                               <UserCheck className="h-4 w-4 mr-1" />
                               {group.lecturer_id === selectedLecturerId ? 'Վերահաստատել' : 
                                (group.secondary_lecturers?.includes(selectedLecturerId) ? 'Արդեն կցված' : 'Կցել')}
                             </Button>
                           </div>
                         </div>
                       ))}
                      {groups.length === 0 && (
                        <p className="text-muted-foreground text-center py-4">
                          Խմբեր չկան
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            Բեռնվում է...
          </div>
        ) : filteredLecturers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchTerm ? 'Դասախոս չի գտնվել' : 'Դասախոսներ դեռ չկան'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/70">
                  <TableHead className="text-muted-foreground">Ավատար</TableHead>
                  <TableHead className="text-muted-foreground">Անուն Ազգանուն</TableHead>
                  <TableHead className="text-muted-foreground">Էլ. փոստ</TableHead>
                  <TableHead className="text-muted-foreground">Հեռախոս</TableHead>
                  <TableHead className="text-muted-foreground">Ստատուս</TableHead>
                  <TableHead className="text-muted-foreground">Խմբեր</TableHead>
                  <TableHead className="text-muted-foreground">Ակտիվ դասեր</TableHead>
                  <TableHead className="text-muted-foreground">Կարգավիճակ</TableHead>
                  <TableHead className="text-muted-foreground">Գրանցման ամսաթիվ</TableHead>
                  <TableHead className="text-muted-foreground text-center">Գործողություններ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                 {filteredLecturers.map((lecturer) => {
                   const isOnline = isUserOnline(lecturer.id);
                   const lastSeen = getLastSeen(lecturer.id);
                  
                  return (
                  <TableRow key={lecturer.id} className="border-border/70">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={lecturer.avatar_url} />
                            <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                              {getInitials(lecturer.first_name, lecturer.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          {isOnline && (
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-gray-800 rounded-full flex items-center justify-center">
                              <Globe className="w-2 h-2 text-foreground" />
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {lecturer.first_name} {lecturer.last_name}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-primary" />
                        {lecturer.email}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {lecturer.phone ? (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-primary" />
                          {lecturer.phone}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Circle 
                          className={`h-3 w-3 transition-all duration-500 ${
                            isOnline 
                              ? 'fill-green-500 text-green-500 animate-pulse drop-shadow-[0_0_6px_#22c55e]' 
                              : 'fill-gray-400 text-muted-foreground'
                          }`}
                        />
                        <span className={`text-sm transition-all duration-500 ${
                          isOnline ? 'text-primary font-semibold' : 'text-muted-foreground'
                        }`}>
                          {isOnline ? 'Օնլայն' : 'Օֆլայն'}
                        </span>
                        {!isOnline && lastSeen && (
                          <span className="text-xs text-muted-foreground animate-fade-in">
                            {lastSeen.toLocaleString('hy-AM', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-blue-500/20 text-blue-300 border-blue-400/30">
                        <Users className="h-3 w-3 mr-1" />
                        {lecturer.groups_count}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-purple-500/20 text-purple-300 border-purple-400/30">
                        <BookOpen className="h-3 w-3 mr-1" />
                        {lecturer.active_lessons}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(lecturer.approval_status)}`}>
                        {getStatusText(lecturer.approval_status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        {formatDate(lecturer.created_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0 text-primary hover:bg-green-500/20">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="border-border/70 bg-card/80">
                          <LecturerPrintCard 
                            lecturer={{
                              id: lecturer.id,
                              first_name: lecturer.first_name,
                              last_name: lecturer.last_name,
                              email: lecturer.email,
                              phone: lecturer.phone,
                              skills: [],
                              avatar_url: lecturer.avatar_url,
                              status: lecturer.approval_status
                            }}
                          />
                          <DropdownMenuItem 
                            onClick={() => handleViewLecturerCredentials(lecturer.id)}
                            className="text-primary focus:text-muted-foreground focus:bg-green-400/10"
                          >
                            <Key className="h-4 w-4 mr-2" />
                            Մուտքի տվյալներ
                          </DropdownMenuItem>
                          {lecturer.approval_status === 'pending' && (
                            <>
                              <DropdownMenuItem 
                                onClick={() => handleApproveLecturer(lecturer.id)}
                                className="text-primary focus:text-muted-foreground focus:bg-green-400/10"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Հաստատել
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleRejectLecturer(lecturer.id)}
                                className="text-red-400 focus:text-red-300 focus:bg-red-400/10"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Մերժել
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem 
                            onClick={() => {
                              setEditingLecturer(lecturer);
                              setIsEditDialogOpen(true);
                            }}
                            className="text-blue-400 focus:text-blue-300 focus:bg-blue-400/10"
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Խմբագրել
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedLecturerId(lecturer.id);
                              setIsAssignGroupDialogOpen(true);
                            }}
                            className="text-yellow-400 focus:text-yellow-300 focus:bg-yellow-400/10"
                          >
                            <UserCheck className="h-4 w-4 mr-2" />
                            Խումբ կցել
                          </DropdownMenuItem>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem
                                onSelect={(e) => e.preventDefault()}
                                className="text-red-400 hover:bg-red-500/20 cursor-pointer"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Ջնջել
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="border-border/70 bg-card/80">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-muted-foreground">
                                  Հաստատել ջնջումը
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-muted-foreground">
                                  Դուք վստահ եք, որ ցանկանում եք ջնջել "{lecturer.first_name} {lecturer.last_name}" դասախոսին? Այս գործողությունը անվերադարձ է:
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-muted border-border/70 text-muted-foreground hover:bg-muted">
                                  Չեղարկել
                                </AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteLecturer(lecturer.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Ջնջել
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                     </TableCell>
                   </TableRow>
                  );
                 })}
               </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
